
import React from 'react'

import { XIcon, InfoIcon } from 'lucide-react'

import { alpha, useTheme, Box, Modal as MuiModal, Fade, Stack, Typography, IconButton } from '@mui/material'

import { useAnimatedHeight } from '../../../hooks/useAnimatedHeight'

interface ModalProps {
  title: string
  /**
   * Accessible name for a modal with no visible title. Only consulted when
   * `title` is empty — a titled modal is named by its own heading element.
   *
   * The fallback exists because the alternative is worse: emitting an
   * `aria-labelledby` that points at nothing leaves the dialog anonymous to a
   * screen reader while the markup claims otherwise, which is harder to notice
   * than having no name at all.
   */
  ariaLabel?: string
  /**
   * Optional glyph rendered immediately LEFT of the title. Distinct from
   * `titleAdornment`, which renders to the right: an identity icon belongs
   * before the name it identifies, so the modal header reads as the same object
   * as the menu row that opened it.
   */
  titleIcon?: React.ReactNode
  /** Optional node rendered immediately right of the title (e.g. a step badge). */
  titleAdornment?: React.ReactNode
  /**
   * Explanatory copy under the title, above the body.
   *
   * Owned by `Modal` rather than typed into each modal's children, because three call
   * sites had independently hand-rolled the identical
   * `<Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5, mb: 5 }}>`
   * — the second hand-roll is the trigger to extract (§36), and the third is how a gap
   * ends up being 20px in three places and 24px in the fourth.
   *
   * The 20px they all used was too tight against the first field's LABEL beneath it: at
   * that distance the description reads as belonging to the field rather than to the
   * modal. 32px is the app's existing description -> first-content value (§40 measures it
   * on the page header), so this matches a rhythm that already exists instead of
   * inventing a fourth number.
   *
   * `lineHeight: 1.5` per §15 — descriptive copy gets breathing room so it does not read
   * dense.
   */
  description?: React.ReactNode
  children: React.ReactNode
  tooltip?: any
  hideBackdrop?: boolean
  open: boolean
  onClose: () => void
  sx?: object
  /** Merged into the scrollable body Box's sx — e.g. trim the generous bottom
   *  padding when the body is a scroll list that should butt closer to the footer. */
  bodySx?: object
  /** Merged into the footer Stack's sx, mirroring `bodySx` — e.g. give the action
   *  row more room when its content is a button rather than plain text. */
  actionsSx?: object
  actions?: React.ReactNode
}

export const Modal = ({ title, ariaLabel, titleIcon, titleAdornment, open, onClose, description, children, tooltip = false, hideBackdrop = false, sx = {}, bodySx = {}, actionsSx = {}, actions }: ModalProps) => {
  const theme = useTheme()

  // The dialog's accessible name. `aria-labelledby` takes a space-separated list of
  // element IDs, so the previous `aria-labelledby={title}` fed it the title STRING:
  // "Archive company" parsed as two references, to elements `Archive` and `company`,
  // neither of which exists. The name resolved to nothing and every modal in both apps
  // was anonymous to assistive tech. It also sat on MUI's root, which is
  // `role="presentation"` — so even a valid id there could not have named the dialog.
  //
  // useId gives a value that is stable across renders and unique per instance, which
  // matters because several modals can be mounted at once (only one visible). The
  // colons React puts in it are legal in an HTML id and resolve fine by getElementById;
  // they are only invalid in a CSS selector, which nothing here uses.
  const reactId = React.useId()
  const titleId = `${reactId}-title`
  const hasVisibleTitle = title.trim().length > 0

  // Animate the modal's height when its content changes size — a step advancing, an
  // Alert appearing, a skeleton resolving. Without this the dialog snaps, and because
  // it is centred with translate(-50%, -50%) the snap moves the TOP edge too, so the
  // whole modal hops under the pointer. The hook is a no-op under
  // prefers-reduced-motion and never runs on first open. See useAnimatedHeight.
  //
  // It is a CALLBACK ref, and that is load-bearing here rather than a style choice. This
  // hook is called while rendering `<MuiModal open={false}>`, which renders no children —
  // so a `useRef` + `useEffect` pair (the first version) found `ref.current === null`, bailed,
  // and never re-ran when the modal actually opened. A callback ref fires when the node
  // attaches, which for a portalled + transitioned child is later than its owner's mount.
  //
  // It still lands on the Box that Fade clones; MUI's Fade forks the child ref rather than
  // replacing it, so both survive.
  const paperRef = useAnimatedHeight<HTMLDivElement>()

  return (
    <MuiModal open={open} onClose={onClose} closeAfterTransition hideBackdrop={hideBackdrop}>
      <Fade in={open} appear>
      <Box
        ref={paperRef}
        // MUI's base `Modal` — unlike its `Dialog` — supplies no `role` and no
        // `aria-modal`; it only contributes the focus trap, Escape handling, focus
        // restore, and `tabIndex={-1}` on this element. So the dialog semantics have to
        // be declared here, on the element that IS the dialog, rather than on MUI's
        // outer `role="presentation"` wrapper.
        role="dialog"
        aria-modal="true"
        // A modal with no visible title gets `aria-label`; one with neither gets
        // neither, because a dangling reference is worse than an absent name.
        {...(hasVisibleTitle
          ? { 'aria-labelledby': titleId }
          : ariaLabel
            ? { 'aria-label': ariaLabel }
            : {})}
        sx={{
          position: 'absolute' as const,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 533,
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'calc(100vh - 64px)',
          // `paper`, not `default`. A modal is a RAISED surface, and the two tokens now
          // mean levels rather than "page colour" and "slightly-off-page colour":
          // `default` is the recessed tone (the page, and field fills), `paper` is anything
          // lifted above it (cards, modals, menus). While `default` was white in light mode
          // this read the same either way; once the page became tinted, a modal on `default`
          // sat at the SAME level as the fields inside it — measured 1.000:1, so a textarea
          // in this modal was invisible except for its border.
          bgcolor: 'background.paper',
          borderRadius: 2,
          // Mode-aware hairline border so the modal has a defined edge against the
          // darkened backdrop in dark mode (shadow alone reads as fuzzy there).
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.shadows[24],
          outline: 'none',
          display: 'flex',
          flexDirection: 'column',
          ...sx
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            p: 4,
            pb: 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
            flexShrink: 0
          }}
        >
          <Stack direction="row" alignItems="center" spacing={3}>
            {titleIcon}
            <Typography id={titleId} variant="body1" sx={{ fontSize: '20px', fontWeight: 400 }}>{title}</Typography>
            {titleAdornment}
            {tooltip && <InfoIcon size={20} strokeWidth={1} color={theme.palette.grey[800]} style={{cursor: 'pointer'}}/>}
          </Stack>

          <IconButton
            color="error"
            // Its only content is an SVG glyph, so without this the dialog's close
            // control reaches a screen reader as an unnamed button.
            aria-label="Close"
            onClick={onClose}
            sx={{
              color: theme.palette.text.primary,
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: alpha(theme.palette.grey[500] || theme.palette.text.primary, 0.08),
                color: theme.palette.text.primary
              }            
            }}
          >
            <XIcon strokeWidth={1} />
          </IconButton>
        </Stack>
        
        <Box
          sx={{
            p: 4,
            pt: 5, // more breathing room between the header bar and the first content
            pb: 7, // generous bottom gap so the last field isn't cramped against the footer/edge
            overflow: 'auto',
            flex: 1,
            minHeight: 0, // Important for flex scrolling
            ...bodySx
          }}
        >
          {description && (
            <Typography
              color="text.secondary"
              /*
                Type properties set EXPLICITLY rather than via `variant="body3"`.
                `packages/ui` compiles without the app's MUI Typography module
                augmentation, so `body3`/`body4` are not assignable here — `FieldLabel`
                and `NavSectionLabel` both hit this same wall and record the same
                reasoning. 14px/1.5 is what body3 resolves to in the app.

                32px bottom, not the 20 the call sites used — see the `description` prop's
                own note. Absolute px because this is a layer-1 component and the two apps
                have different spacing bases (§29/§47), so a multiplier would render at
                double size in apps/app.
              */
              sx={{ display: 'block', fontSize: 14, lineHeight: 1.5, mb: '32px' }}
            >
              {description}
            </Typography>
          )}
          {children}
        </Box>

        {actions && (
          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={3}
            sx={{
              p: 4,
              pt: 3,
              borderTop: `1px solid ${theme.palette.divider}`,
              flexShrink: 0,
              ...actionsSx
            }}
          >
            {actions}
          </Stack>
        )}
      </Box>
      </Fade>
    </MuiModal>
  )
}
